import {
  Badge,
  Col,
  Input,
  Popover,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VuiPageTitle from "../../../components/page-title";
import { useTranslation } from "react-i18next";
import VuiButton from "../../../components/button";
import {
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table/interface";
import { LeadDataList } from "./interface";
import VuiTableActionButton from "../../../components/table-action-button";
import { useGetLead } from "../hooks/use-get-lead";
import VuiAccessible from "../../../components/accessible";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import {
  overrideTableSortOrder,
  snakeCaseToTitleCase,
  uiFilterData,
} from "../../../utils/helpers";
import VuiSelect from "../../../components/select";
import { ValueType } from "../../../components/select/interface";
import {
  DEFAULT_PAGE_SIZE,
  LEAD_STATUS_COLOR,
  LOCALSTORAGE_FILTER_DATA_KEY,
} from "../../../constants";
import _ from "lodash";
import useDebounce from "../../../hooks/use-debounce";
import IndustryRepository from "../../../repositories/industry-repository";
import UserRepository from "../../../repositories/user-repository";
import moment from "moment";
import ConstantRepository from "../../../repositories/constant-repository";
import TabPane from "antd/lib/tabs/TabPane";

const qs = require("qs-lite");

const { Text } = Typography;

type LeadTabType =
  | "default"
  | "prospect"
  | "qualified_opportunity"
  | "solution_fit"
  | "vendor_of_choice"
  | "terms_agreed"
  | "closed_won"
  | "closed_lost";

const tabs: LeadTabType[] = [
  "default",
  "prospect",
  "qualified_opportunity",
  "solution_fit",
  "vendor_of_choice",
  "terms_agreed",
  "closed_won",
  "closed_lost",
];

const LeadListModule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data,
    loadData,
    isOnFetchingData,
    totalData,
    countData,
    loadCountData,
    isOnFetchingCountData,
  } = useGetLead();
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);

  const [selectedIndustry, setSelectedIndustry] = useState<
    ValueType | undefined
  >();

  const [selectedPriority, setSelectedPriority] = useState<
    ValueType | undefined
  >();

  const [selectedUser, setSelectedUser] = useState<ValueType | undefined>();

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy") || "ascend",
    order_by: (searchParams.get("orderBy") as string) || "last_activity_date",
    with: [
      "customer.type",
      "customer.industry",
      "industry",
      "priority",
      "assign",
      "type",
    ],
  });

  const [selectedTab, setSelectedTab] = useState<LeadTabType>(
    (searchParams.get("tab") as LeadTabType) || "default"
  );

  const countByStatus = useCallback(
    (status: string) => {
      if (status === "DEFAULT") {
        return countData.reduce((acc, item) => acc + item.count, 0);
      }
      return countData.find((item) => item.status === status)?.count || 0;
    },
    [countData, totalData]
  );

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    if (selectedIndustry) {
      Object.assign(params, {
        industry: selectedIndustry.value,
      });
    } else {
      const recentIndustry = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.industry
      );
      if (!recentIndustry) {
        searchParams.delete("industry");
      } else {
        const paramsIndustryIds = _.map(
          searchParams.getAll("industry"),
          (item) => parseInt(item)
        );

        if (paramsIndustryIds.length) {
          const industryFromStorage = uiFilterData.getSelectedFilter(
            recentIndustry,
            paramsIndustryIds
          );

          setSelectedIndustry(industryFromStorage[0]);

          Object.assign(params, {
            industry: industryFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.industry);
        }
      }
    }

    if (selectedPriority) {
      Object.assign(params, {
        priority: selectedPriority.value,
      });
    } else {
      const recentPriority = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.priority
      );
      if (!recentPriority) {
        searchParams.delete("priority");
      } else {
        const paramsPriorityIds = _.map(
          searchParams.getAll("priority"),
          (item) => parseInt(item)
        );

        if (paramsPriorityIds.length) {
          const priorityFromStorage = uiFilterData.getSelectedFilter(
            recentPriority,
            paramsPriorityIds
          );

          setSelectedPriority(priorityFromStorage[0]);

          Object.assign(params, {
            priority: priorityFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.priority);
        }
      }
    }

    if (selectedUser) {
      Object.assign(params, {
        user: selectedUser.value,
      });
    } else {
      const recentUser = uiFilterData.get(LOCALSTORAGE_FILTER_DATA_KEY.user);
      if (!recentUser) {
        searchParams.delete("user");
      } else {
        const paramsUserIds = _.map(searchParams.getAll("user"), (item) =>
          parseInt(item)
        );

        if (paramsUserIds.length) {
          const userFromStorage = uiFilterData.getSelectedFilter(
            recentUser,
            paramsUserIds
          );

          setSelectedUser(userFromStorage[0]);

          Object.assign(params, {
            user: userFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.user);
        }
      }
    }

    Object.assign(params, {
      tab: selectedTab,
    });

    if (tableQuery.with) {
      Object.assign(params, {
        with: tableQuery.with,
      });
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/lead");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.order_by
        ? { order_by: tableQuery.order_by }
        : { order_by: "created_at" }),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : { sorted_by: "DESC" }),
      ...(params.industry ? { industry: params.industry } : {}),
      ...(params.priority ? { priority: params.priority } : {}),
      ...(params.user ? { user: params.user } : {}),
      ...(params.tab ? { status: params.tab } : {}),
      with: tableQuery.with,
    };

    if (params.tab === "default") {
      tableParams.exclude_status = ["won", "lost"];
    }

    await loadData(tableParams);

    setHasInitialize(true);
  }, [
    tableQuery,
    debouncedSearch,
    selectedIndustry,
    selectedPriority,
    selectedUser,
    selectedTab,
    loadData,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  const columns: ColumnsType<LeadDataList> = [
    {
      title: t("common.text.date"),
      dataIndex: "date",
      key: "date",
      sorter: true,
      width: 110,
      defaultSortOrder: tableQuery.order_by === "date" && tableQuery.sorted_by,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: t("common.text.client"),
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      render: (text, record) => {
        const clientType = record.customer?.type?.label
          ? record.customer.type.label
          : record.type?.label;
        const clientPrefix = clientType !== "None" ? clientType : "";

        return (
          <Space style={{ whiteSpace: "nowrap" }}>
            <Text>{clientPrefix + " " + text}</Text>
            {record.assign && (
              <Tag style={{ color: "#757575" }}>{record.assign.initial}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "#",
      dataIndex: "priority",
      key: "priority",
      width: 20,
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "priority" && tableQuery.sorted_by,
      render: (text) => {
        let iconSrc;

        switch (text?.label) {
          case "High":
            iconSrc = "/assets/icons/high-priority.svg";
            break;
          case "Medium":
            iconSrc = "/assets/icons/medium-priority.svg";
            break;
          case "Low":
            iconSrc = "/assets/icons/low-priority.svg";
            break;
          default:
            iconSrc = "";
        }

        return (
          <Popover content={<Text>{text?.label}</Text>}>
            {iconSrc && <img src={iconSrc} alt={text?.label} width="18px" />}
          </Popover>
        );
      },
    },
    {
      title: t("common.text.industry"),
      dataIndex: "industry",
      key: "industry",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "industry" && tableQuery.sorted_by,
      render: (text, record) => (
        <Text>{text?.name ?? record.customer?.industry?.name}</Text>
      ),
    },
    {
      title: t("common.text.lastUpdate"),
      dataIndex: "last_activity_date",
      key: "last_activity_date",
      width: 155,
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "last_activity_date" && tableQuery.sorted_by,
      render: (date, record) => (
        <Space size="small" align="center">
          <Tooltip title={moment(date).format("ddd, DD MMM YY")}>
            <Text>{moment(date).fromNow()}</Text>
          </Tooltip>
          {record.last_activity_note && (
            <Popover
              placement="top"
              content={
                <div style={{ maxWidth: "300px" }}>
                  {record.last_activity_note}
                </div>
              }
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                <InfoCircleOutlined style={{ cursor: "pointer" }} />
              </span>
            </Popover>
          )}
        </Space>
      ),
    },
    {
      title: t("common.text.status"),
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text) => (
        <Tag color={_.get(LEAD_STATUS_COLOR, text, "blue")}>
          {snakeCaseToTitleCase(text)}
        </Tag>
      ),
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 50,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/lead/${record.id}`}
            detailPermissionName="lead.show"
            showDetail={record.can_show}
            openNewTab={true}
          />
        );
      },
    },
  ];

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
        with: tableQuery.with,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );

  const handleOnChangeIndustry = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.industry, [value]);
    setSelectedIndustry(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      industry: value,
    }));
  }, []);

  const handleOnChangeUser = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.user, [value]);
    setSelectedUser(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      user: value,
    }));
  }, []);

  const handleOnChangePriority = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.priority, [value]);
    setSelectedPriority(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      priority: value,
    }));
  }, []);

  const handleTabChange = (key: string) => {
    setSelectedTab(key as LeadTabType);

    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      tab: key,
    }));
  };

  useEffect(() => {
    (async () => {
      await loadCountData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <VuiPageTitle title={t("pages.lead.list.title")}>
        <VuiAccessible access="lead.store">
          <Link to="/lead/add">
            <VuiButton
              title={t("common.button.addItem", {
                item: t("common.text.leads"),
              })}
              buttonProps={{
                icon: <PlusOutlined />,
                size: "middle",
              }}
            />
          </Link>
        </VuiAccessible>
      </VuiPageTitle>

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            size="middle"
            onChange={(value) => {
              setSearch(value.target.value);
            }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={IndustryRepository}
            value={selectedIndustry}
            placeholder={t("common.text.selectIndustry")}
            onChange={handleOnChangeIndustry}
            className="select-filter"
            size="middle"
          />
        </Col>
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={UserRepository}
            value={selectedUser}
            placeholder={t("common.text.selectAssignee")}
            onChange={handleOnChangeUser}
            className="select-filter"
            size="middle"
          />
        </Col>
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={ConstantRepository}
            value={selectedPriority}
            placeholder={t("common.text.selectPriority")}
            onChange={handleOnChangePriority}
            repositoryParams={{
              for: "priority_lead",
            }}
            labelKey="label"
            className="select-filter"
            size="middle"
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey={selectedTab} onChange={handleTabChange}>
        {tabs.map((tab) => {
          return (
            <TabPane
              tab={
                <Space align="center" size={5}>
                  {!["default", "won", "lost"].includes(tab) &&
                    countByStatus(tab.toUpperCase()) > 0 && (
                      <Badge
                        style={{ marginBottom: "2px" }}
                        overflowCount={99}
                        color={_.get(
                          LEAD_STATUS_COLOR,
                          tab.toUpperCase(),
                          "blue"
                        )}
                        count={countByStatus(tab.toUpperCase())}
                        size="small"
                        offset={[0, 0]}
                      />
                    )}
                  <Text>
                    {tab === "default" ? "All" : snakeCaseToTitleCase(tab)}
                  </Text>
                </Space>
              }
              key={tab}
            >
              <Table
                rowKey={"id"}
                columns={columns}
                dataSource={data}
                loading={isOnFetchingData}
                onChange={handleTableChange}
                pagination={{
                  current: tableQuery.page,
                  showSizeChanger: true,
                  pageSize: tableQuery.per_page,
                  total: totalData,
                }}
                size="small"
                scroll={{ x: "max-content" }}
              />
            </TabPane>
          );
        })}
      </Tabs>
    </>
  );
};
export default LeadListModule;
